import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { RoleGate, NotAuthorized } from '@/components/auth';
import AccessRequestsPanel from '@/components/AccessRequestsPanel';
import {
  Users,
  Plus,
  Search,
  MoreHorizontal,
  Shield,
  Edit,
  Trash2,
  UserCheck,
  UserX,
  Building2,
  MapPin,
  Briefcase,
  Activity,
  Clock,
  ArrowUp,
  ArrowDown,
  ChevronsUpDown,
  Mail,
  Copy,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { apiV1 } from '@/api/client';
import { GRANTABLE_PERMISSIONS, toggleGrant } from '@/lib/permissions';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const roleColors = {
  admin: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300',
  editor: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  viewer: 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300',
};

const statusColors = {
  active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  suspended: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  deactivated: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300',
  invited: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
};

// SSO users have no real mailbox, so the backend stores a non-routable
// placeholder identity in the RFC 2606 reserved `example.com` zone
// (e.g. `smid01@alchemy-sso.example.com`). Show it cleanly as
// `smid01@alchemy-sso` — the `.example.com` suffix is an internal detail.
const displayIdentity = (email) =>
  typeof email === 'string'
    ? email.replace(/@alchemy-sso\.example\.com$/i, '@alchemy-sso')
    : email;

// The page filters, counts and sorts on the client, so it asks for every
// user rather than the API's default page of 50.
const USER_FETCH_LIMIT = 5000;

const ROLE_RANK = { admin: 0, editor: 1, viewer: 2 };

// Most people sorting by "Last seen" want the most recent first.
const SORT_DEFAULT_DIR = { last_seen: 'desc' };

const restrictionCount = (user) => {
  if (user.role === 'admin') return -1; // Full access sorts ahead of "no restrictions"
  const r = user.access_restrictions || {};
  return (r.allowed_regions?.length || 0) + (r.allowed_offices?.length || 0)
    + (r.allowed_office_types?.length || 0) + (r.allowed_agents?.length || 0);
};

const SORT_VALUE = {
  user: (u) => (u.full_name || u.email || '').toLowerCase(),
  role: (u) => ROLE_RANK[u.role] ?? 99,
  status: (u) => u.status || '',
  // Never signed in counts as the oldest.
  last_seen: (u) => {
    const t = u.last_login_at ? new Date(u.last_login_at).getTime() : NaN;
    return Number.isNaN(t) ? -Infinity : t;
  },
  restrictions: restrictionCount,
};

const compareUsers = ({ key, dir }) => (a, b) => {
  const va = SORT_VALUE[key](a);
  const vb = SORT_VALUE[key](b);
  const cmp = va < vb ? -1 : va > vb ? 1 : 0;
  if (cmp !== 0) return dir === 'asc' ? cmp : -cmp;
  return SORT_VALUE.user(a).localeCompare(SORT_VALUE.user(b));
};

function SortableHead({ column, label, sort, onSort, className }) {
  const active = sort.key === column;
  const Icon = !active ? ChevronsUpDown : sort.dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <TableHead
      className={className}
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() => onSort(column)}
        className="inline-flex items-center gap-1 hover:text-foreground"
        data-testid={`sort-${column}`}
      >
        {label}
        <Icon className={`w-3.5 h-3.5 ${active ? '' : 'opacity-40'}`} />
      </button>
    </TableHead>
  );
}

export default function UserManagement() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  // Inactivity filter pill: all / inactive_30 / inactive_60 / never.
  // Drives the table filter AND the colour of the "Last seen" cell.
  const [inactivityFilter, setInactivityFilter] = useState('all');
  const [totalUsers, setTotalUsers] = useState(null);
  const [sort, setSort] = useState({ key: 'user', dir: 'asc' });
  
  // Create/Edit dialog state
  const [showDialog, setShowDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [approveTarget, setApproveTarget] = useState(null);
  const [approveRole, setApproveRole] = useState('viewer');
  const [approving, setApproving] = useState(false);
  // External partner invites (backend/auth/partners.py).
  const [partnerDialog, setPartnerDialog] = useState(false);
  const [partnerForm, setPartnerForm] = useState({ email: '', full_name: '' });
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState(null); // { email, invite_url, emailed, expires_at }
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'viewer',
    extra_permissions: [],
    access_restrictions: {
      allowed_regions: [],
      allowed_offices: [],
      allowed_office_types: [],
      allowed_agents: [],
    },
  });
  
  // Reference data for restrictions
  const [regions, setRegions] = useState([]);
  const [offices, setOffices] = useState([]);
  const [agents, setAgents] = useState([]);
  
  // Load users
  const loadUsers = useCallback(async () => {
    try {
      const response = await apiV1.get(
        `/users?limit=${USER_FETCH_LIMIT}&search=${encodeURIComponent(searchQuery)}`,
      );
      if (response.data?.success) {
        setUsers(response.data.data?.users || []);
        setTotalUsers(response.data.data?.total ?? null);
      }
    } catch (error) {
      console.error('Failed to load users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);
  
  // Load reference data for restrictions
  const loadReferenceData = useCallback(async () => {
    try {
      const [regionsRes, officesRes, agentsRes] = await Promise.all([
        fetch(`${API_URL}/api/v1/reference-data/options?entity_type=region`),
        fetch(`${API_URL}/api/v1/reference-data/options?entity_type=office`),
        fetch(`${API_URL}/api/v1/reference-data/options?entity_type=agent`),
      ]);
      
      const [regionsData, officesData, agentsData] = await Promise.all([
        regionsRes.json(),
        officesRes.json(),
        agentsRes.json(),
      ]);
      
      // Data is nested under data.{entity_type}
      if (regionsData.success) setRegions(regionsData.data?.region || []);
      if (officesData.success) setOffices(officesData.data?.office || []);
      if (agentsData.success) setAgents(agentsData.data?.agent || []);
    } catch (error) {
      console.error('Failed to load reference data:', error);
    }
  }, []);
  
  useEffect(() => {
    loadUsers();
    loadReferenceData();
  }, [loadUsers, loadReferenceData]);
  
  // Handle create/edit user
  const handleSave = async () => {
    try {
      const url = editingUser 
        ? `/users/${editingUser.id}`
        : `/users`;
      
      // Build payload
      const payload = editingUser
        ? {
            full_name: formData.full_name,
            role: formData.role,
            access_restrictions: formData.access_restrictions,
            // The list replaces the stored grants; [] revokes them all.
            extra_permissions: formData.extra_permissions || [],
          }
        : formData;
      
      const response = editingUser
        ? await apiV1.patch(url, payload)
        : await apiV1.post(url, payload);
      
      if (response.data?.success) {
        toast.success(editingUser ? 'User updated!' : 'User created!');
        setShowDialog(false);
        resetForm();
        loadUsers();
      } else {
        toast.error(response.data?.detail?.message || 'Failed to save user');
      }
    } catch (error) {
      console.error('Failed to save user:', error);
      
      // Extract meaningful error message
      const errorData = error.response?.data;
      let errorMessage = 'Failed to save user';
      
      // Handle Pydantic validation errors (array of errors)
      if (Array.isArray(errorData?.detail)) {
        const messages = errorData.detail.map(err => {
          const field = err.loc?.[err.loc.length - 1] || 'field';
          return `${field}: ${err.msg}`;
        });
        errorMessage = messages.join('. ');
      } 
      // Handle custom validation errors (object with code and message)
      else if (errorData?.detail?.message) {
        errorMessage = errorData.detail.message;
      }
      // Handle string error detail
      else if (typeof errorData?.detail === 'string') {
        errorMessage = errorData.detail;
      }
      
      toast.error(errorMessage);
    }
  };
  
  // Open the approve-user dialog for a pending user. Defaults the role
  // select to whatever the SSO gate provisionally set (usually 'editor').
  const openApproveDialog = (user) => {
    setApproveTarget(user);
    setApproveRole(user.role || 'viewer');
  };

  const showInvite = (email, data) => {
    setInviteResult({ email, ...data });
    toast.success(data.emailed ? `Invite emailed to ${email}` : 'Invite created. Copy the link below to send it.');
  };

  const submitPartnerInvite = async () => {
    setInviting(true);
    try {
      const response = await apiV1.post('/users/partners', partnerForm);
      showInvite(partnerForm.email, response.data?.data || {});
      setPartnerForm({ email: '', full_name: '' });
      loadUsers();
    } catch (error) {
      toast.error(error?.message || 'Failed to invite partner');
    } finally {
      setInviting(false);
    }
  };

  const resendPartnerInvite = async (user) => {
    try {
      const response = await apiV1.post(`/users/${user.id}/partner-invite`);
      setPartnerDialog(true);
      showInvite(user.email, response.data?.data || {});
    } catch (error) {
      toast.error(error?.message || 'Failed to send a new invite');
    }
  };

  const copyInviteLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteResult.invite_url);
      toast.success('Link copied');
    } catch {
      toast.error('Could not copy. Select the link and copy it yourself.');
    }
  };

  // Submit the approval — POSTs to the new /users/:id/approve endpoint.
  const submitApprove = async () => {
    if (!approveTarget) return;
    setApproving(true);
    try {
      const response = await apiV1.post(
        `/users/${approveTarget.id}/approve`,
        { role: approveRole },
      );
      if (response.data?.success) {
        toast.success(`${approveTarget.full_name || approveTarget.email} approved as ${approveRole}`);
        setApproveTarget(null);
        loadUsers();
      } else {
        toast.error(response.data?.detail?.message || 'Failed to approve user');
      }
    } catch (error) {
      const message =
        error?.response?.data?.detail?.message ||
        error?.message ||
        'Failed to approve user';
      toast.error(message);
    } finally {
      setApproving(false);
    }
  };

  // Handle deactivate user
  const handleDeactivate = async (userId) => {
    if (!confirm('Are you sure you want to deactivate this user?')) return;
    
    try {
      const response = await apiV1.delete(`/users/${userId}`);
      
      if (response.data?.success) {
        toast.success('User deactivated');
        loadUsers();
      } else {
        toast.error(response.data?.detail?.message || 'Failed to deactivate user');
      }
    } catch (error) {
      toast.error('Failed to deactivate user');
    }
  };
  
  // Reset form
  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      full_name: '',
      role: 'viewer',
      extra_permissions: [],
      access_restrictions: {
        allowed_regions: [],
        allowed_offices: [],
        allowed_office_types: [],
        allowed_agents: [],
      },
    });
    setEditingUser(null);
  };
  
  // Open edit dialog
  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '',
      full_name: user.full_name,
      role: user.role,
      extra_permissions: user.extra_permissions || [],
      access_restrictions: user.access_restrictions || {
        allowed_regions: [],
        allowed_offices: [],
        allowed_office_types: [],
        allowed_agents: [],
      },
    });
    setShowDialog(true);
  };
  
  // Toggle array item
  const toggleArrayItem = (field, value) => {
    setFormData(prev => {
      const currentArray = prev.access_restrictions[field] || [];
      const newArray = currentArray.includes(value)
        ? currentArray.filter(v => v !== value)
        : [...currentArray, value];
      
      return {
        ...prev,
        access_restrictions: {
          ...prev.access_restrictions,
          [field]: newArray,
        },
      };
    });
  };
  
  // Check if user has restrictions
  const hasRestrictions = (user) => {
    const r = user.access_restrictions || {};
    return (
      (r.allowed_regions?.length > 0) ||
      (r.allowed_offices?.length > 0) ||
      (r.allowed_office_types?.length > 0) ||
      (r.allowed_agents?.length > 0)
    );
  };
  
  // ---- Inactivity helpers (U3 usage reporting) -------------------------
  // Days since the user's last login, or null if they've never signed in.
  const daysSinceLogin = (user) => {
    if (!user?.last_login_at) return null;
    const last = new Date(user.last_login_at).getTime();
    if (Number.isNaN(last)) return null;
    return Math.floor((Date.now() - last) / 86400000);
  };

  const matchesInactivityFilter = (user, filter) => {
    if (filter === 'all') return true;
    const days = daysSinceLogin(user);
    if (filter === 'never') return days === null;
    if (filter === 'inactive_30') return days !== null && days >= 30;
    if (filter === 'inactive_60') return days !== null && days >= 60;
    return true;
  };

  const inactiveCount60d = users.filter(
    (u) => matchesInactivityFilter(u, 'inactive_60') || daysSinceLogin(u) === null,
  ).length;

  const formatLastSeen = (user) => {
    const days = daysSinceLogin(user);
    if (days === null) return 'Never';
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 30) return `${days}d ago`;
    if (days < 365) return `${Math.floor(days / 30)}mo ago`;
    return `${Math.floor(days / 365)}y ago`;
  };

  // Filter users by search + inactivity pill.
  const filteredUsers = users
    .filter(u =>
      u.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchQuery.toLowerCase()),
    )
    .filter(u => matchesInactivityFilter(u, inactivityFilter))
    .sort(compareUsers(sort));

  const handleSort = (column) => {
    setSort((prev) => (
      prev.key === column
        ? { key: column, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key: column, dir: SORT_DEFAULT_DIR[column] || 'asc' }
    ));
  };
  
  // Check if current user is admin
  const isAdmin = currentUser?.role === 'admin';
  
  if (!isAdmin) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-12 text-center">
            <Shield className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
            <p className="text-muted-foreground">
              You don't have permission to access user management.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <RoleGate
      role="admin"
      fallback={
        <NotAuthorized
          title="User management is admin-only"
          description="Editors and viewers can browse the people directory through their own areas, but only administrators can add, edit, or remove users."
        />
      }
    >
    <div className="p-6 space-y-6" data-testid="user-management-page">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold">User Management</h1>
          <p className="text-muted-foreground">
            Manage users and their access restrictions
          </p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setShowDialog(true);
          }}
          data-testid="create-user-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add User
        </Button>
        <Button
          variant="outline"
          className="ml-2"
          onClick={() => { setInviteResult(null); setPartnerDialog(true); }}
          data-testid="invite-partner-btn"
        >
          <Mail className="w-4 h-4 mr-2" />
          Invite partner
        </Button>
      </div>

      {/* Pending access requests (Alchemy SSO gate) */}
      <AccessRequestsPanel />

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              <span className="text-2xl font-bold">{totalUsers ?? users.length}</span>
            </div>
            <p className="text-sm text-muted-foreground">Total Users</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-rose-500" />
              <span className="text-2xl font-bold">
                {users.filter(u => u.role === 'admin').length}
              </span>
            </div>
            <p className="text-sm text-muted-foreground">Admins</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-green-500" />
              <span className="text-2xl font-bold">
                {users.filter(u => u.status === 'active').length}
              </span>
            </div>
            <p className="text-sm text-muted-foreground">Active</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-amber-500" />
              <span className="text-2xl font-bold">
                {users.filter(u => hasRestrictions(u)).length}
              </span>
            </div>
            <p className="text-sm text-muted-foreground">With Restrictions</p>
          </CardContent>
        </Card>
        <Card
          className="cursor-pointer hover:border-primary transition-colors"
          onClick={() => setInactivityFilter('inactive_60')}
          data-testid="stat-card-inactive"
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-600" />
              <span className="text-2xl font-bold">{inactiveCount60d}</span>
            </div>
            <p className="text-sm text-muted-foreground">Inactive 60d+ / Never</p>
          </CardContent>
        </Card>
      </div>

      {/* Search + inactivity filter pills */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="search-users-input"
          />
        </div>
        <div className="flex items-center gap-1" data-testid="inactivity-filter">
          {[
            { value: 'all',          label: 'All' },
            { value: 'inactive_30',  label: 'Inactive 30d+' },
            { value: 'inactive_60',  label: 'Inactive 60d+' },
            { value: 'never',        label: 'Never signed in' },
          ].map((opt) => (
            <Button
              key={opt.value}
              size="sm"
              variant={inactivityFilter === opt.value ? 'default' : 'outline'}
              onClick={() => setInactivityFilter(opt.value)}
              data-testid={`inactivity-filter-${opt.value}`}
            >
              {opt.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Users Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <SortableHead column="user" label="User" sort={sort} onSort={handleSort} />
                <SortableHead column="role" label="Role" sort={sort} onSort={handleSort} />
                <SortableHead column="status" label="Status" sort={sort} onSort={handleSort} />
                <SortableHead column="last_seen" label="Last seen" sort={sort} onSort={handleSort} />
                <SortableHead column="restrictions" label="Access Restrictions" sort={sort} onSort={handleSort} />
                <TableHead className="w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => (
                  <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{user.full_name}</p>
                        <p className="text-sm text-muted-foreground">{displayIdentity(user.email)}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={roleColors[user.role]}>
                        {user.role}
                      </Badge>
                      {user.role !== 'admin' &&
                        GRANTABLE_PERMISSIONS.filter((p) => (user.extra_permissions || []).includes(p.key)).map((p) => (
                          <Badge key={p.key} variant="outline" className="ml-1 text-xs" data-testid={`grant-${user.id}-${p.key}`}>
                            {p.badge || p.label}
                          </Badge>
                        ))}
                    </TableCell>
                    <TableCell>
                      <Badge className={statusColors[user.status]}>
                        {user.status}
                      </Badge>
                      {user.auth_method === 'partner' && (
                        <Badge variant="outline" className="ml-1 text-xs" data-testid={`partner-badge-${user.id}`}>
                          Partner
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell data-testid={`last-seen-${user.id}`}>
                      {(() => {
                        const days = daysSinceLogin(user);
                        const label = formatLastSeen(user);
                        // Colour matches the inactivity bands in the filter:
                        // never → muted-foreground, 60d+ → amber, 30d+ → yellow,
                        // recent → default.
                        let className = 'text-sm';
                        if (days === null) className += ' text-muted-foreground italic';
                        else if (days >= 60)  className += ' text-amber-600 font-medium';
                        else if (days >= 30)  className += ' text-yellow-600';
                        return (
                          <span className={className}>
                            {label}
                            {user.login_count > 0 && days !== null && (
                              <span className="ml-2 text-xs text-muted-foreground">
                                ({user.login_count} sign-in{user.login_count === 1 ? '' : 's'})
                              </span>
                            )}
                          </span>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      {user.role === 'admin' ? (
                        <span className="text-sm text-muted-foreground">Full Access</span>
                      ) : hasRestrictions(user) ? (
                        <div className="flex flex-wrap gap-1">
                          {user.access_restrictions?.allowed_regions?.length > 0 && (
                            <Badge variant="outline" className="text-xs">
                              <MapPin className="w-3 h-3 mr-1" />
                              {user.access_restrictions.allowed_regions.length} regions
                            </Badge>
                          )}
                          {user.access_restrictions?.allowed_offices?.length > 0 && (
                            <Badge variant="outline" className="text-xs">
                              <Building2 className="w-3 h-3 mr-1" />
                              {user.access_restrictions.allowed_offices.length} offices
                            </Badge>
                          )}
                          {user.access_restrictions?.allowed_office_types?.length > 0 && (
                            <Badge variant="outline" className="text-xs">
                              <Briefcase className="w-3 h-3 mr-1" />
                              {user.access_restrictions.allowed_office_types.join(', ')}
                            </Badge>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">No restrictions</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Actions</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem asChild>
                            <Link
                              to={`/admin/users/${user.id}/activity`}
                              data-testid={`view-activity-${user.id}`}
                            >
                              <Activity className="w-4 h-4 mr-2" />
                              View activity
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleEdit(user)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          {user.status === 'pending' && (
                            <DropdownMenuItem
                              onClick={() => openApproveDialog(user)}
                              data-testid={`approve-user-${user.id}`}
                            >
                              <UserCheck className="w-4 h-4 mr-2" />
                              Approve…
                            </DropdownMenuItem>
                          )}
                          {user.auth_method === 'partner' && user.status !== 'deactivated' && (
                            <DropdownMenuItem
                              onClick={() => resendPartnerInvite(user)}
                              data-testid={`resend-invite-${user.id}`}
                            >
                              <Mail className="w-4 h-4 mr-2" />
                              {user.status === 'invited' ? 'Resend invite' : 'Send password reset link'}
                            </DropdownMenuItem>
                          )}
                          {user.id !== currentUser?.id && (
                            <DropdownMenuItem
                              onClick={() => handleDeactivate(user.id)}
                              className="text-destructive"
                            >
                              <UserX className="w-4 h-4 mr-2" />
                              Deactivate
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {editingUser ? 'Edit User' : 'Create User'}
            </DialogTitle>
            <DialogDescription>
              {editingUser
                ? 'Update user details and access restrictions'
                : 'Add a new user to the system'}
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="details" className="flex-1 overflow-hidden flex flex-col">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="details">User Details</TabsTrigger>
              <TabsTrigger value="restrictions">Access Restrictions</TabsTrigger>
            </TabsList>

            <ScrollArea className="flex-1 mt-4">
              <TabsContent value="details" className="space-y-4 pr-4">
                <div className="space-y-2">
                  <Label htmlFor="full_name">Full Name</Label>
                  <Input
                    id="full_name"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    placeholder="John Doe"
                    data-testid="user-fullname-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john@example.com"
                    disabled={!!editingUser}
                    data-testid="user-email-input"
                  />
                </div>

                {!editingUser && (
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="Min 8 characters"
                      data-testid="user-password-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Min 8 characters with uppercase, lowercase, number & special character
                    </p>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="role">Role</Label>
                  <Select
                    value={formData.role}
                    onValueChange={(value) => setFormData({ ...formData, role: value })}
                  >
                    <SelectTrigger data-testid="user-role-select">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">
                        <div className="flex items-center gap-2">
                          <Shield className="w-4 h-4 text-rose-500" />
                          Admin - Full access to all features
                        </div>
                      </SelectItem>
                      <SelectItem value="editor">
                        <div className="flex items-center gap-2">
                          <Edit className="w-4 h-4 text-emerald-500" />
                          Editor - Create and manage content
                        </div>
                      </SelectItem>
                      <SelectItem value="viewer">
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-sky-500" />
                          Viewer - View-only access
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {formData.role === 'admin' && (
                  <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                    <p className="text-sm text-amber-800 dark:text-amber-300">
                      <Shield className="w-4 h-4 inline mr-2" />
                      Admin users have full access to all data and bypass all restrictions.
                    </p>
                  </div>
                )}

                {/* Per-user grants on top of the role (audiences:export, and
                    audiences:export:national under it). */}
                <div className="space-y-3" data-testid="extra-permissions">
                  <Label>Additional access</Label>
                  {GRANTABLE_PERMISSIONS.map((perm) => {
                    const isAdminRole = formData.role === 'admin';
                    const checked =
                      isAdminRole || (formData.extra_permissions || []).includes(perm.key);
                    return (
                      <div
                        key={perm.key}
                        className={`flex items-start gap-3${perm.requires ? ' ml-7' : ''}`}
                      >
                        <Checkbox
                          id={`perm-${perm.key}`}
                          checked={checked}
                          disabled={isAdminRole}
                          data-testid={`perm-${perm.key}`}
                          onCheckedChange={(on) =>
                            // Ticking National ticks the base grant too;
                            // unticking the base drops National with it.
                            setFormData((prev) => ({
                              ...prev,
                              extra_permissions: toggleGrant(prev.extra_permissions, perm.key, !!on),
                            }))
                          }
                        />
                        <div className="space-y-0.5">
                          <label htmlFor={`perm-${perm.key}`} className="text-sm font-medium">
                            {perm.label}
                          </label>
                          <p className="text-xs text-muted-foreground">
                            {perm.description}
                            {isAdminRole && ' Admins always have this.'}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </TabsContent>

              <TabsContent value="restrictions" className="space-y-6 pr-4">
                {formData.role === 'admin' ? (
                  <div className="p-8 text-center">
                    <Shield className="w-12 h-12 mx-auto text-amber-500 mb-4" />
                    <h3 className="text-lg font-semibold mb-2">Admin users have full access</h3>
                    <p className="text-muted-foreground">
                      Access restrictions don't apply to admin users. They can see all data.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                      <p className="text-sm text-blue-800 dark:text-blue-300">
                        Leave all sections empty to give this user access to all data.
                        Select specific items to restrict their view to only that data.
                      </p>
                    </div>

                    {/* Office Types */}
                    <div className="space-y-3">
                      <Label className="flex items-center gap-2">
                        <Briefcase className="w-4 h-4" />
                        Office Types
                      </Label>
                      <div className="flex gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={formData.access_restrictions.allowed_office_types?.includes('branch')}
                            onCheckedChange={() => toggleArrayItem('allowed_office_types', 'branch')}
                          />
                          <span>Branch</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={formData.access_restrictions.allowed_office_types?.includes('franchise')}
                            onCheckedChange={() => toggleArrayItem('allowed_office_types', 'franchise')}
                          />
                          <span>Franchise</span>
                        </label>
                      </div>
                    </div>

                    {/* Regions */}
                    <div className="space-y-3">
                      <Label className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        Allowed Regions ({formData.access_restrictions.allowed_regions?.length || 0} selected)
                      </Label>
                      <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto p-2 border rounded-lg">
                        {regions.length === 0 ? (
                          <p className="text-sm text-muted-foreground col-span-2 p-2">No regions available</p>
                        ) : (
                          regions.map((region) => (
                            <label
                              key={region.id}
                              className="flex items-center gap-2 cursor-pointer p-2 hover:bg-muted rounded"
                            >
                              <Checkbox
                                checked={formData.access_restrictions.allowed_regions?.includes(region.id)}
                                onCheckedChange={() => toggleArrayItem('allowed_regions', region.id)}
                              />
                              <span className="text-sm">{region.name}</span>
                            </label>
                          ))
                        )}
                      </div>
                    </div>

                    {/* Offices */}
                    <div className="space-y-3">
                      <Label className="flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        Allowed Offices ({formData.access_restrictions.allowed_offices?.length || 0} selected)
                      </Label>
                      <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto p-2 border rounded-lg">
                        {offices.length === 0 ? (
                          <p className="text-sm text-muted-foreground col-span-2 p-2">No offices available</p>
                        ) : (
                          offices.map((office) => (
                            <label
                              key={office.id}
                              className="flex items-center gap-2 cursor-pointer p-2 hover:bg-muted rounded"
                            >
                              <Checkbox
                                checked={formData.access_restrictions.allowed_offices?.includes(office.id)}
                                onCheckedChange={() => toggleArrayItem('allowed_offices', office.id)}
                              />
                              <span className="text-sm truncate" title={office.name}>
                                {office.name}
                              </span>
                            </label>
                          ))
                        )}
                      </div>
                    </div>

                    {/* Agents */}
                    <div className="space-y-3">
                      <Label className="flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Allowed Agents ({formData.access_restrictions.allowed_agents?.length || 0} selected)
                      </Label>
                      <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto p-2 border rounded-lg">
                        {agents.length === 0 ? (
                          <p className="text-sm text-muted-foreground col-span-2 p-2">No agents available</p>
                        ) : (
                          agents.map((agent) => (
                            <label
                              key={agent.id}
                              className="flex items-center gap-2 cursor-pointer p-2 hover:bg-muted rounded"
                            >
                              <Checkbox
                                checked={formData.access_restrictions.allowed_agents?.includes(agent.id)}
                                onCheckedChange={() => toggleArrayItem('allowed_agents', agent.id)}
                              />
                              <span className="text-sm truncate" title={agent.name}>
                                {agent.name}
                              </span>
                            </label>
                          ))
                        )}
                      </div>
                    </div>
                  </>
                )}
              </TabsContent>
            </ScrollArea>
          </Tabs>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} data-testid="save-user-btn">
              {editingUser ? 'Update User' : 'Create User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve pending user dialog */}
      <Dialog
        open={!!approveTarget}
        onOpenChange={(open) => { if (!open) setApproveTarget(null); }}
      >
        <DialogContent data-testid="approve-user-dialog">
          <DialogHeader>
            <DialogTitle>Approve user</DialogTitle>
            <DialogDescription>
              {approveTarget && (
                <>
                  Activate <strong>{approveTarget.full_name || approveTarget.email}</strong> and
                  grant them a role. For Alchemy SSO users, an allowlist rule is added so they
                  won't re-enter the pending state on their next sign-in.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="approve-role">Role</Label>
            <Select value={approveRole} onValueChange={setApproveRole}>
              <SelectTrigger id="approve-role" data-testid="approve-role-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="viewer">Viewer — read-only</SelectItem>
                <SelectItem value="editor">Editor — create + edit content</SelectItem>
                <SelectItem value="admin">Admin — full access</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveTarget(null)} disabled={approving}>
              Cancel
            </Button>
            <Button onClick={submitApprove} disabled={approving} data-testid="approve-user-submit">
              {approving ? 'Approving…' : 'Approve'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite an external partner (agency) */}
      <Dialog open={partnerDialog} onOpenChange={(open) => { setPartnerDialog(open); if (!open) setInviteResult(null); }}>
        <DialogContent data-testid="invite-partner-dialog">
          <DialogHeader>
            <DialogTitle>{inviteResult ? 'Invite ready' : 'Invite an external partner'}</DialogTitle>
            <DialogDescription>
              {inviteResult
                ? `${inviteResult.emailed ? 'We emailed this link to' : 'Email could not be sent. Send this link to'} ${inviteResult.email}. It works once and expires on ${new Date(inviteResult.expires_at).toLocaleDateString()}.`
                : 'For agencies working with Pam Golding Properties. Partners are editors who see what they create plus national content, never lead contact details, and sign in with email and a password they set from the invite.'}
            </DialogDescription>
          </DialogHeader>
          {inviteResult ? (
            <div className="space-y-2 py-2">
              <Label htmlFor="invite-url">Invite link</Label>
              <div className="flex gap-2">
                <Input id="invite-url" readOnly value={inviteResult.invite_url} data-testid="invite-url" />
                <Button variant="outline" onClick={copyInviteLink} aria-label="Copy link">
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label htmlFor="partner-name">Full name</Label>
                <Input id="partner-name" value={partnerForm.full_name}
                  onChange={(e) => setPartnerForm({ ...partnerForm, full_name: e.target.value })}
                  data-testid="partner-name-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="partner-email">Email</Label>
                <Input id="partner-email" type="email" value={partnerForm.email}
                  onChange={(e) => setPartnerForm({ ...partnerForm, email: e.target.value })}
                  data-testid="partner-email-input" />
              </div>
            </div>
          )}
          <DialogFooter>
            {inviteResult ? (
              <Button onClick={() => { setPartnerDialog(false); setInviteResult(null); }}>Done</Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setPartnerDialog(false)} disabled={inviting}>
                  Cancel
                </Button>
                <Button onClick={submitPartnerInvite}
                  disabled={inviting || !partnerForm.email.trim() || !partnerForm.full_name.trim()}
                  data-testid="invite-partner-submit">
                  {inviting ? 'Inviting…' : 'Send invite'}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </RoleGate>
  );
}
